import { shovelAiApi, SyllabusImportStatusItem } from '@api/shovelAiApi';
import { softCloseHover } from '@components/common/dialog/softCloseHover';
import MaterialIcon from '@components/common/icons/MaterialIcon';
import { Column, FlexRow, textOverflowStyle } from '@components/common/layoutUtils';
import { calendarActions } from '@state/calendar';
import { CalendarCourse } from '@state/calendar/types';
import { courseActions } from '@state/course';
import { eventActions } from '@state/events';
import { RootState } from '@state/types';
import { workloadActions } from '@state/workload';
import * as workloadSelectors from '@state/workload/selectors';
import { settingsActions } from '@state/settings';
import { dismissedEstimateCourses } from '@utils/settingsUtils';
import { ESTIMATE_TASKS_BUTTON_ATTRIBUTE, flyIntoButton } from '@utils/flyIntoButton';
import * as routes from '@utils/constants/routes';
import t from '@i18n/t';
import { useRouteMatch } from 'react-router-dom';
import colors from '@utils/colors';
import { removeTimestampFromFilename } from '@utils/syllabusImport';
import { MediumText, RegularText, SmallText } from '@utils/typography';
import { useCallback, useEffect, useRef, useState } from 'react';
import Loader from 'react-loader-spinner';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import ModalDialog from '@components/common/ModalDialog';
import Button from '@components/common/buttons/Button';
import TippyTooltip from '@components/common/tooltip/TippyTooltip';
import TaskEstimatorDialog from './taskEstimator';
import { useAutoTaskCategorization } from './useAutoTaskCategorization';

const POLL_INTERVAL_MS = 10_000;

const jobKey = (item: SyllabusImportStatusItem) => `${item.fileName}::${item.createdAt}`;

const SyllabusImportStatus = () => {
  const dispatch = useDispatch();
  const [items, setItems] = useState<SyllabusImportStatusItem[]>([]);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const handledCompletedKeysRef = useRef<Set<string>>(new Set());
  const initializedHandledRef = useRef(false);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [estimateCourseId, setEstimateCourseId] = useState<number | null>(null);

  const lastSyllabusImportAt = useSelector((state: RootState) => state.course.lastSyllabusImportAt);
  const isLoggedIn = useSelector((state: RootState) => !!state.semester.info?.user?.email);
  const courses = useSelector((state: RootState) => state.calendar.courses);
  const courseIds = new Set(courses.map(c => c.id));
  const { categorizingCourses, estimateCourses: needingEstimates } = useAutoTaskCategorization(courses, isLoggedIn);

  // A course whose box the student closed stays out of it, unless the Estimator's Auto-categorize asked for the nudge.
  const userSettings = useSelector((state: RootState) => state.settings.userSettings);
  const pendingEstimateReminders = useSelector(workloadSelectors.getPendingEstimateReminders);
  const dismissed = dismissedEstimateCourses(userSettings);
  // Closed here: out of the box at once, not only once the server has saved it, which left the box standing behind its
  // own copy as that flew off (Petr: "a ghost of the notification").
  const [closedIds, setClosedIds] = useState<number[]>([]);
  const estimateCourses = needingEstimates.filter(
    course => (!dismissed.has(course.id) && !closedIds.includes(course.id)) || pendingEstimateReminders.includes(course.id)
  );

  // Where the top bar has its "Estimate Tasks" button, the box can be closed: it flies into the button, which has it all.
  const onPageWithEstimateButton = !!useRouteMatch([routes.CALENDAR, routes.TASKS]);
  const panelRef = useRef<HTMLDivElement>(null);
  const estimateSectionRef = useRef<HTMLDivElement>(null);

  const clearPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const refreshAfterImport = useCallback(() => {
    dispatch(courseActions.importCourse.success());
    dispatch(calendarActions.commitmentsData.request());
    dispatch(calendarActions.calendarData.request());
    dispatch(eventActions.sourceEvents.request());
    dispatch(eventActions.getEventsList.request({}));
    dispatch(workloadActions.getWorkload.request(undefined));
  }, [dispatch]);

  const fetchImportStatus = useCallback(async () => {
    const isInitialSnapshot = !initializedHandledRef.current;
    initializedHandledRef.current = true;

    const response = await shovelAiApi.getSyllabusImportStatus();

    if (response.ok && response.data) {
      const newItems = response.data.items;

      setItems(newItems);

      const completedItems = newItems.filter(item => item.status === 'completed');

      if (isInitialSnapshot) {
        completedItems.forEach(item => handledCompletedKeysRef.current.add(jobKey(item)));
      } else {
        const newlyCompleted = completedItems.filter(item => !handledCompletedKeysRef.current.has(jobKey(item)));
        if (newlyCompleted.length > 0) {
          newlyCompleted.forEach(item => handledCompletedKeysRef.current.add(jobKey(item)));
          refreshAfterImport();
        }
      }

      const hasInProgress = newItems.some(item => item.status === 'pending' || item.status === 'in_progress');

      if (!hasInProgress) {
        clearPolling();
      }
    } else {
      clearPolling();
    }
  }, [clearPolling, refreshAfterImport]);

  useEffect(() => {
    if (!isLoggedIn) {
      clearPolling();
      setItems([]);
      handledCompletedKeysRef.current = new Set();
      initializedHandledRef.current = false;
      return;
    }

    clearPolling();
    fetchImportStatus();
    intervalRef.current = setInterval(fetchImportStatus, POLL_INTERVAL_MS);

    return clearPolling;
  }, [isLoggedIn, lastSyllabusImportAt, fetchImportStatus, clearPolling]);

  const acknowledgeSyllabusImport = async (item: SyllabusImportStatusItem) => {
    await shovelAiApi.acknowledgeSyllabusImportResult({
      fileName: item.fileName,
      createdAt: item.createdAt
    });
    setItems(prevItems => prevItems.filter(i => !(i.fileName === item.fileName && i.createdAt === item.createdAt)));
  };

  const jobItems = items.filter(
    item =>
      courseIds.has(item.courseId) &&
      (item.status === 'pending' || item.status === 'in_progress' || item.status === 'failed')
  );

  const hide = jobItems.length === 0 && categorizingCourses.length === 0 && estimateCourses.length === 0;
  // The estimates are all the box shows: closing them closes the box.
  const onlySection = jobItems.length === 0 && categorizingCourses.length === 0;

  const closeEstimates = () => {
    const button = document.querySelector<HTMLElement>(`[${ESTIMATE_TASKS_BUTTON_ATTRIBUTE}]`);
    // The whole box flies when the estimates are all it shows; otherwise only their part, dressed as the box.
    const source = onlySection ? panelRef.current : estimateSectionRef.current;
    const from = source?.getBoundingClientRect();
    const ghost = source?.cloneNode(true) as HTMLElement | undefined;
    if (ghost && !onlySection && from && panelRef.current) {
      const panel = getComputedStyle(panelRef.current);
      Object.assign(ghost.style, {
        padding: panel.padding,
        background: panel.backgroundColor,
        borderRadius: panel.borderRadius,
        boxShadow: panel.boxShadow
      });
    }
    const padded =
      from && !onlySection ? new DOMRect(from.left - 16, from.top - 16, from.width + 32, from.height + 32) : from;

    const ids = new Set(Array.from(dismissed).filter(id => courseIds.has(id)));
    estimateCourses.forEach(course => {
      ids.add(course.id);
      if (pendingEstimateReminders.includes(course.id)) {
        dispatch(workloadActions.setPendingEstimateReminder({ courseId: course.id, pending: false }));
      }
    });
    dispatch(settingsActions.saveUserSettings.request({ dismissedEstimateCourseIds: Array.from(ids) }));
    setClosedIds(closed => [...closed, ...estimateCourses.map(course => course.id)]);

    if (button && ghost && padded) flyIntoButton(ghost, padded, button, t.ESTIMATE_TASKS_ARE_HERE);
  };

  return (
    <>
      <Container hide={hide} ref={panelRef}>
        {jobItems.length > 0 && (
          <Section>
            <RegularText strong>Processing syllabus files</RegularText>
            <Items>
              {jobItems.map(item => {
                const course = courses.find(c => c.id === item.courseId);
                if (!course) {
                  return null;
                }
                return (
                  <JobRow
                    key={`${item.courseId}_${item.fileName}_${item.createdAt}`}
                    item={item}
                    course={course}
                    onViewFailed={() => {
                      acknowledgeSyllabusImport(item);
                      setErrorMessage(
                        item.failureStage === 'import'
                          ? "We're sorry, import failed. Please try again. If it doesn't work, contact us via chat."
                          : "We're sorry, parsing failed. Please try again. If it doesn't work, contact us via chat."
                      );
                    }}
                  />
                );
              })}
            </Items>
          </Section>
        )}
        {categorizingCourses.length > 0 && (
          <Section>
            <RegularText strong>Categorizing tasks</RegularText>
            <Items>
              {categorizingCourses.map(course => (
                <CategorizingRow key={course.id} course={course} />
              ))}
            </Items>
          </Section>
        )}
        {estimateCourses.length > 0 && (
          <Section ref={estimateSectionRef}>
            <SectionTitle>
              <RegularText strong>Estimate tasks</RegularText>
              {onPageWithEstimateButton && (
                // In the box's corner when it closes the box; beside the title when other parts stay.
                <CloseButton inCorner={onlySection} type="button" aria-label={t.CLOSE} onClick={closeEstimates}>
                  <MaterialIcon name="close" size={18} color={colors.gray} />
                </CloseButton>
              )}
            </SectionTitle>
            <Items>
              {estimateCourses.map(course => (
                <EstimateRow
                  key={course.id}
                  course={course}
                  onEstimate={() => {
                    dispatch(workloadActions.setPendingEstimateReminder({ courseId: course.id, pending: false }));
                    setEstimateCourseId(course.id);
                  }}
                />
              ))}
            </Items>
          </Section>
        )}
      </Container>
      <ErrorDialog message={errorMessage} onClose={() => setErrorMessage(null)} />
      <TaskEstimatorDialog
        open={estimateCourseId != null}
        courseId={estimateCourseId}
        onClose={() => setEstimateCourseId(null)}
      />
    </>
  );
};

export default SyllabusImportStatus;

const JobRow = ({
  item,
  course,
  onViewFailed
}: {
  item: SyllabusImportStatusItem;
  course: CalendarCourse;
  onViewFailed: () => void;
}) => {
  return (
    <Item>
      {item.status === 'pending' && (
        <TippyTooltip
          target={
            <StatusIconWrapper>
              <StatusIcon name="hourglass_empty" size={16} />
            </StatusIconWrapper>
          }
          content={'File will be processed soon'}
        />
      )}
      {item.status === 'in_progress' && (
        <TippyTooltip
          target={
            <StatusIconWrapper>
              <Loader type="Oval" color={colors.primaryPurple} height={16} width={16} />
            </StatusIconWrapper>
          }
          content={'File is being processed'}
        />
      )}
      {item.status === 'failed' && (
        <TippyTooltip
          target={
            <StatusIconWrapper>
              <StatusIcon name="error_outline" size={16} color={colors.negative} />
            </StatusIconWrapper>
          }
          content={'File processig failed'}
        />
      )}
      <TippyTooltip target={<CourseIndicator color={course.colorHex} />} content={course.name} />
      <ItemName>{removeTimestampFromFilename(item.fileName)}</ItemName>
      {item.status === 'failed' && (
        <ViewItemButton onClick={onViewFailed}>
          <MediumText color={colors.primaryPurple}>View</MediumText>
        </ViewItemButton>
      )}
    </Item>
  );
};

const CategorizingRow = ({ course }: { course: CalendarCourse }) => {
  return (
    <Item>
      <TippyTooltip
        target={
          <StatusIconWrapper>
            <Loader type="Oval" color={colors.primaryPurple} height={16} width={16} />
          </StatusIconWrapper>
        }
        content={'Categorizing tasks'}
      />
      <TippyTooltip target={<CourseIndicator color={course.colorHex} />} content={course.name} />
      <ItemName>{course.name}</ItemName>
    </Item>
  );
};

const EstimateRow = ({ course, onEstimate }: { course: CalendarCourse; onEstimate: () => void }) => {
  return (
    <Item>
      <TippyTooltip target={<CourseIndicator color={course.colorHex} />} content={course.name} />
      <ItemName>{course.name}</ItemName>
      <ViewItemButton onClick={onEstimate}>
        <MediumText color={colors.primaryPurple}>Estimate</MediumText>
      </ViewItemButton>
    </Item>
  );
};

const ErrorDialog = ({ message, onClose }: { message: string | null; onClose: () => void }) => {
  return (
    <ModalDialog
      open={!!message}
      onClose={() => onClose()}
      size={'sm'}
      title={'Syllabus Import Failed'}
      contentStyle={{ padding: '0 20px', minHeight: '250px', height: '250px' }}
      fullWidth
    >
      <Column style={{ gap: 32 }}>
        <RegularText>{message}</RegularText>
        <FlexRow style={{ justifyContent: 'center' }}>
          <Button onClick={onClose}>OK</Button>
        </FlexRow>
      </Column>
    </ModalDialog>
  );
};

const Container = styled(Column)<{ hide: boolean }>`
  position: fixed;
  // bottom: 80px;
  bottom: 120px;
  right: 24px;
  gap: 12px;
  width: 300px;
  border-radius: 8px;
  padding: 16px;
  background-color: ${props => props.theme.background};
  box-shadow: 0 6px 20px 20px ${({ theme }) => theme.boxShadow};
  z-index: 10;
  ${props => props.hide && 'display: none;'};
`;

const Section = styled(Column)`
  gap: 12px;
`;

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const CloseButton = styled.button<{ inCorner: boolean }>`
  display: flex;
  ${({ inCorner }) => (inCorner ? 'position: absolute; top: 8px; right: 8px;' : 'margin: -4px -6px -4px 0;')}
  padding: 3px;
  border: none;
  border-radius: 4px;
  background: none;
  cursor: pointer;
  ${softCloseHover({ spread: 0 })}
`;

const Items = styled(Column)`
  gap: 8px;
`;

const Item = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

const ItemName = styled(SmallText)`
  ${textOverflowStyle};

  flex: 1;
`;

const CourseIndicator = styled.div<{ color: string }>`
  width: 10px;
  height: 10px;
  border-radius: 5px;
  background-color: ${props => props.color};
  flex-shrink: 0;
`;

const ViewItemButton = styled.button`
  outline: none;
  cursor: pointer;
  border: 1px solid ${props => props.theme.primary} !important;
  padding: 2px 6px;
  border-radius: 5px;
  font-size: 12px;
  font-weight: 600;
  min-width: 48px;
  max-width: 100px;
  background-color: transparent;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:hover {
    span {
      color: ${props => props.theme.primary};
    }
  }
`;

const StatusIcon = styled(MaterialIcon)`
  cursor: default !important;
`;

const StatusIconWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;
